import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';

import { Close, Save } from '@mui/icons-material';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormGroup,
	IconButton,
	LinearProgress,
	Typography,
} from '@mui/material';

import { currency, getErrorMessage } from 'lib/helpers';
import {
	NFC_BATCH_MAX_CARDS,
	NfcCardLoadResult,
	assignCardProducts,
	assignCardsProducts,
	getCardFailures,
	getLoadTotal,
} from 'lib/models/nfcCards';

import useIsMobile from 'hooks/useIsMobile';
import useNfcCardCart from 'hooks/useNfcCardCart';

import NfcCardCartPicker from 'components/NfcCardCartPicker';
import NfcCardFailures from 'components/NfcCardFailures';

type Props = {
	/** The ids of the cards to load: one from the card detail, many from the listing. */
	cardIds: string[];
	/** Called with true when something was loaded, so the caller can refetch. */
	onClose: (loaded: boolean) => void;
};

type LoadError = { message: string; failures: { index: number; reason: string }[] | null };

/**
 * Loads the same products on one or many cards, as one order per card that the bar hands
 * over when the card is tapped.
 *
 * The cart is picked with `NfcCardCartPicker`. The total shown before saving is only an
 * estimate: the one shown after is the one the server priced.
 *
 * The batch key is drawn once, when the modal opens, so a double click or a retry after a
 * lost answer gets back the orders already written instead of loading the cards twice.
 */
export default function NfcCardProductsModal({ cardIds, onClose }: Props) {
	const { t } = useTranslation();
	const isMobile = useIsMobile();

	const [batchKey] = useState(() => `nfc-load-${nanoid()}`);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<LoadError | null>(null);
	const [results, setResults] = useState<NfcCardLoadResult[] | null>(null);

	const cart = useNfcCardCart();

	const loading = cart.loading || saving;

	const section = cart.section;

	const tooManyCards = cardIds.length > NFC_BATCH_MAX_CARDS;

	const canSave =
		cardIds.length > 0 && !!section && cart.cart.length > 0 && !cart.missingMixer && !tooManyCards && !loading;

	const save = async () => {
		if (!canSave || !section?._id) return;

		setSaving(true);
		setError(null);

		const payload = { section: section._id, products: cart.cart, total: cart.total, batchKey };

		try {
			const loaded =
				cardIds.length === 1 ? await assignCardProducts(cardIds[0], payload) : await assignCardsProducts(cardIds, payload);
			setResults(loaded);
		} catch (loadError) {
			const failures = getCardFailures(loadError);
			setError({ message: getErrorMessage(loadError), failures });

			// A refusal that is not about the cards is about the catalogue (a price, a title, a
			// product out of the section): read it again so the cart shows what it is now
			if (!failures) {
				cart.refetchCatalogue();
			}
		}

		setSaving(false);
	};

	const created = results?.filter((result) => result.created).length ?? 0;
	const serverTotal = getLoadTotal(results?.[0]?.orders);

	return (
		<Dialog open onClose={() => !saving && onClose(!!results)} fullWidth maxWidth="md" fullScreen={isMobile}>
			{loading && <LinearProgress />}
			<DialogTitle
				sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
			>
				<div>{t('nfc:loadTitle', { count: cardIds.length })}</div>
				<IconButton aria-label={t('common:close')} onClick={() => !saving && onClose(!!results)} edge="end">
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{results ? (
					<FormGroup sx={{ paddingTop: 1 }}>
						<Alert severity="success" sx={{ mb: 2 }}>
							<Typography>{t('nfc:loadedCount', { count: results.length })}</Typography>
							{typeof serverTotal === 'number' && (
								<Typography>{t('nfc:loadedTotal', { total: currency(serverTotal) })}</Typography>
							)}
						</Alert>

						{created < results.length && (
							<Alert severity="info" sx={{ mb: 2 }}>
								{t('nfc:loadRetried', { count: results.length - created })}
							</Alert>
						)}
					</FormGroup>
				) : (
					<FormGroup sx={{ paddingTop: 1 }}>
						{tooManyCards && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								{t('nfc:loadTooMany', { max: NFC_BATCH_MAX_CARDS })}
							</Alert>
						)}

						{!!error && <NfcCardFailures message={error.message} failures={error.failures} />}

						<NfcCardCartPicker cart={cart} disabled={saving} onSectionChange={() => setError(null)} />
					</FormGroup>
				)}
			</DialogContent>

			{/* Out of the scrolling content, so on a phone they stay at the bottom, where the thumb reaches them */}
			<DialogActions
				sx={{
					flexDirection: isMobile ? 'column' : 'row',
					alignItems: 'stretch',
					gap: 1,
					padding: 2,
					'& > :not(:first-of-type)': { marginLeft: 0 },
				}}
			>
				{results ? (
					<Button
						variant="contained"
						color="secondary"
						size="large"
						fullWidth={isMobile}
						startIcon={<Close />}
						onClick={() => onClose(true)}
					>
						{t('common:close')}
					</Button>
				) : (
					<>
						<Button
							variant="contained"
							color="secondary"
							size="large"
							fullWidth={isMobile}
							startIcon={<Close />}
							onClick={() => onClose(false)}
							disabled={saving}
						>
							{t('common:cancel')}
						</Button>

						<Button
							variant="contained"
							color="primary"
							size="large"
							fullWidth={isMobile}
							startIcon={<Save />}
							onClick={save}
							disabled={!canSave}
						>
							{t('nfc:loadSave')}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}
